import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Inbox as InboxIcon,
  Send as SentIcon,
  Drafts as DraftsIcon,
  AllInbox as AllIcon,
  Phone as PhoneIcon,
  Settings as SettingsIcon,
  ContentCopy as CopyIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { EmailAccountStatus, PhoneNumber, FolderType } from './types';

interface ChannelSidebarProps {
  accounts: EmailAccountStatus[];
  phoneNumbers: PhoneNumber[];
  selectedAccountId: string | null; // null = "All Inboxes"
  onSelectAccount: (accountId: string | null) => void;
  onManageAccounts: () => void;
  totalUnread: number;
  folder: FolderType;
  onFolderChange: (folder: FolderType) => void;
  draftCount: number;
}

const DEEP_TEAL = '#1A5F7A';

const ChannelSidebar: React.FC<ChannelSidebarProps> = ({
  accounts,
  phoneNumbers,
  selectedAccountId,
  onSelectAccount,
  onManageAccounts,
  totalUnread,
  folder,
  onFolderChange,
  draftCount,
}) => {
  const copyPhone = (phone: string) => {
    navigator.clipboard.writeText(phone);
  };

  const folders: { key: FolderType; label: string; icon: React.ReactNode; badge?: number }[] = [
    { key: 'inbox', label: 'Inbox', icon: <InboxIcon fontSize="small" />, badge: totalUnread > 0 ? totalUnread : undefined },
    { key: 'sent', label: 'Sent', icon: <SentIcon fontSize="small" /> },
    { key: 'drafts', label: 'Drafts', icon: <DraftsIcon fontSize="small" />, badge: draftCount > 0 ? draftCount : undefined },
    { key: 'all', label: 'All Mail', icon: <AllIcon fontSize="small" /> },
  ];

  return (
    <Box
      sx={{
        width: 200,
        minWidth: 200,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        overflow: 'auto',
      }}
    >
      {/* Folders */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
          Folders
        </Typography>
      </Box>
      <List disablePadding dense>
        {folders.map(f => (
          <ListItemButton
            key={f.key}
            selected={folder === f.key}
            onClick={() => { onFolderChange(f.key); onSelectAccount(null); }}
            sx={{
              py: 0.75,
              '&.Mui-selected': { bgcolor: `${DEEP_TEAL}14`, '&:hover': { bgcolor: `${DEEP_TEAL}20` } },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: folder === f.key ? DEEP_TEAL : 'inherit' }}>
              {f.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontWeight: folder === f.key ? 700 : 500, fontSize: '0.82rem', color: folder === f.key ? DEEP_TEAL : 'inherit' }}>
                    {f.label}
                  </Typography>
                  {f.badge !== undefined && (
                    <Chip label={f.badge} size="small" color={f.key === 'inbox' ? 'error' : 'default'} sx={{ height: 18, fontSize: '0.65rem', '.MuiChip-label': { px: 0.5 } }} />
                  )}
                </Box>
              }
            />
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ my: 1 }} />

      {/* Email Accounts */}
      <Box sx={{ px: 1.5, pb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
          Accounts
        </Typography>
      </Box>
      <List disablePadding dense>
        {accounts.map((account) => (
          <ListItemButton
            key={account.id}
            selected={selectedAccountId === account.id}
            onClick={() => onSelectAccount(account.id)}
            sx={{ py: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <DotIcon sx={{ fontSize: 14, color: account.color }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.8rem',
                  }}
                >
                  {account.accountName || account.email}
                </Typography>
              }
              secondary={
                <Typography
                  variant="caption"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    fontSize: '0.7rem',
                    color: account.status === 'error' ? 'error.main' : 'text.secondary',
                  }}
                >
                  {account.email}
                </Typography>
              }
            />
          </ListItemButton>
        ))}
      </List>

      {/* Phone Numbers */}
      {phoneNumbers.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ px: 1.5, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Phone Numbers
            </Typography>
          </Box>
          <List disablePadding dense>
            {phoneNumbers.map((phone) => (
              <Box key={phone.id} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 1 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', lineHeight: 1.3 }}>
                    {phone.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {phone.phone_number}
                  </Typography>
                </Box>
                <Tooltip title="Copy number">
                  <IconButton size="small" onClick={() => copyPhone(phone.phone_number)} sx={{ p: 0.25 }}>
                    <CopyIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </List>
        </>
      )}

      {/* Manage Button */}
      <Box sx={{ mt: 'auto', p: 1.5 }}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={onManageAccounts}
          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
        >
          Manage Accounts
        </Button>
      </Box>
    </Box>
  );
};

export default ChannelSidebar;
